import React, { useEffect, useState, useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "../add-lead-dialog/style";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { getPayload, RootState } from "redux/store";
import {
	getActiveLeadEmails,
	getNonActiveLeadEmails,
	getAgentsWithLeads,
} from "redux/selector";
import { validate } from "shared/yup";
import moment from "moment";
import { createLead } from "redux/slice/leads";
import { createLeadPhone } from "redux/slice/lead-phones";
import { createLeadAnniversary } from "redux/slice/lead-anniversaries";
import { createLeadTag } from "redux/slice/lead-tags";
import { createLeadPipeline } from "redux/slice/lead-pipelines";
import { NewLeadPhone } from "model/lead-phone";
import { LeadAnniversaryType } from "type/lead-anniversary";
import { LeadStatus } from "type/lead-status";
import { LeadPipelineType } from "type/lead-pipeline";
import { User } from "model/user";
import LeadFormV2 from "../lead-form-v2";
import { LeadFormState } from "../lead-form/definitions/types";
import { getCreateLeadValidationSchema } from "../lead-form/definitions/schema";

const mapStateToProps = (state: RootState) => ({
	loading: state.leads.loading,
	agents: getAgentsWithLeads(state),
	activeLeadEmails: getActiveLeadEmails(state),
	nonActiveLeadEmails: getNonActiveLeadEmails(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			createLead,
			createLeadPhone,
			createLeadAnniversary,
			createLeadTag,
			createLeadPipeline,
		},
		dispatch
	);

interface Props
	extends ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		WithStyles<typeof styles> {
	open: boolean;
	onClose: () => void;
	user: User;
	onLeadCreated: (leadId: number) => void;
}

const AddLeadDialogV2Component = (props: Props) => {
	const {
		open,
		onClose,
		user,
		agents,
		classes,
		activeLeadEmails,
		nonActiveLeadEmails,
		createLead,
		createLeadPhone,
		createLeadAnniversary,
		createLeadTag,
		createLeadPipeline,
		onLeadCreated,
	} = props;

	const [submitted, setSubmitted] = useState(false);
	const [leadPhones, setLeadPhones] = useState<NewLeadPhone[]>([]);
	const [agentId, setAgentId] = useState<number | null>(
		(user.agent && user.agent.ownsLeads && user.agent.id) || null
	);

	const wasOpen = useRef(open);

	useEffect(() => {
		if (!wasOpen.current && open) {
			setSubmitted(false);
			setAgentId((user.agent && user.agent.ownsLeads && user.agent.id) || null);
			setLeadPhones([]);
		}
		wasOpen.current = open;
	}, [open, user]);

	const getLeadTypeTags = (typeIds: string[] = []) => {
		return typeIds.map((typeId) => ({ text: typeId }));
	};

	const submit = async (formState: LeadFormState) => {
		setSubmitted(true);
		const validationSchema = getCreateLeadValidationSchema({
			activeLeadEmails,
			nonActiveLeadEmails,
		});
		const errors = validate(validationSchema, formState);

		if (!errors && formState.email) {
			const normalizedAgentId =
				formState.agentId === undefined || formState.agentId === null
					? undefined
					: Number(formState.agentId);
			const agent =
				normalizedAgentId !== undefined && !Number.isNaN(normalizedAgentId)
					? agents.find((a) => a.id === normalizedAgentId)
					: undefined;
			const leadTypeTags = getLeadTypeTags(formState.typeIds);
			const newLead = getPayload(
				await createLead({
					lead: {
						name: formState.name,
						email: formState.email,
						email2: formState.email2,
						phone: formState.phone,
						address: formState.address,
						city: formState.city,
						state: formState.state,
						zip: formState.zip,
						agent,
						agentId: Number.isNaN(normalizedAgentId)
							? undefined
							: normalizedAgentId,
						status: LeadStatus.ACTIVE,
						label: formState.name,
						readiness: formState.readiness,
						transactionTimelineStart: formState.transactionTimelineStart,
						transactionTimelineEnd: formState.transactionTimelineEnd,
						justBrowsing: formState.justBrowsing,
						transactionPrice: formState.transactionPrice,
						workingWithAnotherAgent: formState.workingWithAnotherAgent,
						mortgagePreApproval: formState.mortgagePreApproval,
						referralRequested: formState.referralRequested,
					},
				})
			);

			for (const phone of formState.leadPhones) {
				createLeadPhone({
					leadPhone: {
						leadId: newLead.id,
						type: phone.type,
						value: phone.value,
						deleted: phone.deleted,
					},
				});
			}

			if (formState.birthday) {
				createLeadAnniversary({
					leadAnniversary: {
						startDate: moment(formState.birthday as Date),
						type: LeadAnniversaryType.BIRTHDAY,
						deleted: false,
						lead: newLead.id && (newLead as any),
					},
				});
			}

			if (formState.transactionAnniversary) {
				createLeadAnniversary({
					leadAnniversary: {
						startDate: moment(formState.transactionAnniversary as Date),
						type: LeadAnniversaryType.TRANSACTION_ANNIVERSARY,
						deleted: false,
						lead: newLead.id && (newLead as any),
					},
				});
			}

			for (const tag of leadTypeTags) {
				createLeadTag({
					leadTag: {
						leadId: newLead.id,
						text: tag.text,
					},
				});
			}

			createLeadPipeline({
				leadPipeline: {
					leadId: newLead.id,
					type: LeadPipelineType.UNCONTACTED,
				},
			});

			onLeadCreated(newLead.id);
			onClose();
		}
	};

	const canAddPhone = leadPhones.filter((p) => !p.deleted).length < 3;
	const validationSchema = getCreateLeadValidationSchema({
		activeLeadEmails,
		nonActiveLeadEmails,
	});

	return (
		<LeadFormV2
			onClose={onClose}
			open={open}
			isEdit={false}
			submitted={submitted}
			leadPhonesList={leadPhones}
			crmPhones={user && user.permissions.crmPhones}
			crmAnniversary={user && user.permissions.crmAnniversary}
			crmTags={user && user.permissions.crmTags}
			canAddPhone={canAddPhone}
			isBroker={user && user.permissions.broker && user.permissions.showTeamFeatures}
			isAdmin={user && user.admin}
			agents={agents}
			handleSubmit={submit}
			validationSchema={validationSchema}
			defaultAgentId={agentId}
			extendedLeadFields={user && user.permissions.extendedLeadFields}
		/>
	);
};

export const AddLeadDialogV2 = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(AddLeadDialogV2Component),
	styles
);
